import { useState, useEffect, useCallback, useMemo } from 'react';
import { getApiUrl } from '../config';
import { Match, Player, PlayerReview } from '../types';

interface ReviewsState {
  hasReviewedPlayer: { [playerId: string]: boolean };
  loadingReviews: boolean;
  canViewReviews: boolean;
  selectedPlayerForReview: Player | null;
  selectedPlayerForViewReviews: Player | null;
  selectedPlayerForProfile: Player | null;
}

interface ReviewsActions {
  loadReviewedPlayers: () => Promise<void>;
  checkCanViewReviews: () => Promise<void>;
  handleCreateReview: (player: Player) => void;
  handleReviewCreated: (review: PlayerReview) => void;
  handleCloseCreateReview: () => void;
  handleViewPlayerReviews: (player: Player) => void;
  handleClosePlayerReviews: () => void;
  handleCloseReviewsList: () => void;
  handleViewPlayerProfile: (player: Player) => void;
  handleClosePlayerProfile: () => void;
  setSelectedPlayerForReview: (player: Player | null) => void;
  setSelectedPlayerForViewReviews: (player: Player | null) => void;
  setSelectedPlayerForProfile: (player: Player | null) => void;
}

interface UseReviewsProps {
  matchId: string | undefined;
  match: Match | null;
  user: any;
  onShowNotification: (message: string, type: 'success' | 'error') => void;
  onSetShowCreateReviewModal: (show: boolean) => void;
  onSetShowPlayerReviewsModal: (show: boolean) => void;
  onSetShowPlayerProfileModal: (show: boolean) => void;
  onRefetchMatch?: () => void | Promise<void>;
}

export const useReviews = ({
  matchId,
  match,
  user,
  onShowNotification,
  onSetShowCreateReviewModal,
  onSetShowPlayerReviewsModal,
  onSetShowPlayerProfileModal,
  onRefetchMatch
}: UseReviewsProps): ReviewsState & ReviewsActions => {
  const [hasReviewedPlayer, setHasReviewedPlayer] = useState<{ [playerId: string]: boolean }>({});
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [canViewReviews, setCanViewReviews] = useState(false);
  const [selectedPlayerForReview, setSelectedPlayerForReview] = useState<Player | null>(null);
  const [selectedPlayerForViewReviews, setSelectedPlayerForViewReviews] = useState<Player | null>(null);
  const [selectedPlayerForProfile, setSelectedPlayerForProfile] = useState<Player | null>(null);

  // API call with authentication
  const apiCallWithAuth = useCallback(async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('authToken');
    const headers = {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...options.headers
    };

    return fetch(url, { 
      ...options, 
      headers
    });
  }, []);

  // Verificar si el usuario puede ver reviews (debe haber dejado al menos 1 review)
  const checkCanViewReviews = useCallback(async () => {
    if (!matchId || !user?.id || match?.status !== 'FINISHED') return;
    
    try {
      const response = await apiCallWithAuth(getApiUrl(`/api/reviews/match/${matchId}/teammates/${user.id}`));
      
      if (response.ok) {
        const data = await response.json();
        setCanViewReviews(data.reviewsGiven > 0);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error checking can view reviews:', error);
      setCanViewReviews(false);
    }
  }, [matchId, user?.id, match?.status, apiCallWithAuth]);

  // Cargar qué jugadores ya han sido reviewados por el usuario actual
  const loadReviewedPlayers = useCallback(async () => {
    if (!matchId || !user?.id || match?.status !== 'FINISHED') return;
    
    try {
      setLoadingReviews(true);
      
      const response = await apiCallWithAuth(getApiUrl(`/api/reviews/user/${user.id}/match/${matchId}`));
      
      if (response.ok) {
        const data = await response.json();
        const reviewedPlayers: { [playerId: string]: boolean } = {};
        
        Object.keys(data.reviewedPlayers).forEach(playerId => {
          reviewedPlayers[playerId] = data.reviewedPlayers[playerId].hasReviewed;
        });
        
        setHasReviewedPlayer(reviewedPlayers);
      } else {
        setHasReviewedPlayer({});
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error loading reviewed players:', error);
      setHasReviewedPlayer({});
    } finally {
      setLoadingReviews(false);
    }
  }, [matchId, user?.id, match?.status, apiCallWithAuth]);

  // Funciones para manejar reviews
  const handleCreateReview = useCallback((player: Player) => {
    if (loadingReviews) {
      onShowNotification('Espera a que se carguen las reviews del partido', 'error');
      return;
    }
    
    setSelectedPlayerForReview(player);
    onSetShowCreateReviewModal(true);
  }, [loadingReviews, onShowNotification, onSetShowCreateReviewModal]);

  const handleReviewCreated = useCallback((review: PlayerReview) => {
    onShowNotification('Review creada exitosamente', 'success');
    setSelectedPlayerForReview(null);
    onSetShowCreateReviewModal(false);

    // Actualizar el estado de jugadores reviewados
    if (selectedPlayerForReview) {
      setHasReviewedPlayer(prev => ({
        ...prev,
        [selectedPlayerForReview.id]: true
      }));
    }

    // Habilitar inmediatamente el botón "Ver Reviews" después de crear una review
    setCanViewReviews(true);

    // Refrescar datos del partido para que la lista y badges se actualicen
    Promise.resolve(onRefetchMatch?.()).then(() => loadReviewedPlayers());
  }, [onShowNotification, selectedPlayerForReview, onSetShowCreateReviewModal, onRefetchMatch, loadReviewedPlayers]);

  const handleCloseCreateReview = useCallback(() => {
    setSelectedPlayerForReview(null);
    onSetShowCreateReviewModal(false);
  }, [onSetShowCreateReviewModal]);

  const handleViewPlayerReviews = useCallback((player: Player) => {
    setSelectedPlayerForViewReviews(player);
    onSetShowPlayerReviewsModal(true);
  }, [onSetShowPlayerReviewsModal]);

  const handleClosePlayerReviews = useCallback(() => {
    setSelectedPlayerForViewReviews(null);
    onSetShowPlayerReviewsModal(false);
  }, [onSetShowPlayerReviewsModal]);

  const handleCloseReviewsList = useCallback(() => {
    // This would be handled by the parent component
  }, []);

  const handleViewPlayerProfile = useCallback((player: Player) => {
    setSelectedPlayerForProfile(player);
    onSetShowPlayerProfileModal(true);
  }, [onSetShowPlayerProfileModal]);

  const handleClosePlayerProfile = useCallback(() => {
    setSelectedPlayerForProfile(null);
  }, []);

  // Load data when dependencies change
  useEffect(() => {
    checkCanViewReviews();
  }, [checkCanViewReviews]);

  useEffect(() => {
    loadReviewedPlayers();
  }, [loadReviewedPlayers]);

  return {
    // State
    hasReviewedPlayer,
    loadingReviews,
    canViewReviews,
    selectedPlayerForReview,
    selectedPlayerForViewReviews,
    selectedPlayerForProfile,
    
    // Actions
    loadReviewedPlayers,
    checkCanViewReviews,
    handleCreateReview,
    handleReviewCreated,
    handleCloseCreateReview,
    handleViewPlayerReviews,
    handleClosePlayerReviews,
    handleCloseReviewsList,
    handleViewPlayerProfile,
    handleClosePlayerProfile,
    setSelectedPlayerForReview,
    setSelectedPlayerForViewReviews,
    setSelectedPlayerForProfile
  };
};
